import { ChangeDetectionStrategy, Component, DestroyRef, EnvironmentInjector, inject, Injector, OnInit, runInInjectionContext } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';

import { TranslateModule } from '@ngx-translate/core';
import { filter, take } from 'rxjs';
import { AResourceService } from '../../../services/resources/a-resource.service';
import { CustomNotificationService } from '../../../services/utils/custom-notification.service';
import { SpinnerComponent } from '../spinner/spinner.component';
import { toObservable } from '@angular/core/rxjs-interop';
import { BreadcrumbService } from '../../../services/utils/breadcrumb.service';
import { FormatHelper } from '../../../helpers/format-helper';
import { ModelService } from '../../../services/utils/model.service';

@Component({
  selector: 'app-breadcrumbs',
  imports: [RouterLink, TranslateModule, SpinnerComponent],
  templateUrl: './breadcrumbs.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './breadcrumbs.component.scss',
})
export class BreadcrumbsComponent implements OnInit {
  routePaths: string[] = [];
  private environmentInjector = inject(EnvironmentInjector);
  private readonly destroyRef = inject(DestroyRef);
  baseParents: string[] = [];
  validParentPaths: string[] = [
    'search',
    'admin',
    'users',
    'mail-accounts',
    'settings',
  ];

  constructor(
    private router: Router,
    private notificationService: CustomNotificationService,
    public breadcrumbService: BreadcrumbService,
    private injector: Injector,
    private modelService: ModelService,
  ) {}

  ngOnInit() {
    this.setRoutePaths();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.setRoutePaths();
        },
      });
  }

  setRoutePaths() {
    this.routePaths = this.router.url.split('/').filter((p) => p != '');
    this.initBreadcrumbs();
  }

  initBreadcrumbs() {
    let parents: {
      text: string;
      path: string;
      service: AResourceService<any>;
    }[] = [];
    let parent: {
      text: string;
      path: string;
      service: AResourceService<any>;
      identifier: string;
    } | null = null;
    let currentPath = '';

    this.breadcrumbService.reset();

    this.routePaths.forEach((path) => {
      let text = '';
      let loading = false;
      let newParent;
      let isSubParent = false;
      if ((newParent = this.isNotConcreteModel(path))) {
        parent = newParent;

        isSubParent = this.isSubParent(parents, path);
        parents.push(newParent);

        text = `NAVIGATION.${FormatHelper.toSnakeCase(newParent.text).toUpperCase()}`;
        newParent.path = currentPath + `/${newParent.path}`;
      } else {
        // get concrete model
        if (parent) {
          // Check if the path is a number before assuming it's a model ID
          const id = Number(path);
          if (!Number.isInteger(id)) {
            // It's likely a sub-page like "stock-bookings" – allow it
            parent = null;
            currentPath += `/${path}`;
            this.breadcrumbService.add({
              text: `NAVIGATION.${path.toUpperCase().replace(/-/g, '_')}`,
              path: currentPath,
              loading: false,
            });
            return;
          }
          let concreteModel = this.getConcreteModel(id, parent);
          if (concreteModel) {
            text = this.modelService.getModelLabel(
              concreteModel,
              parent.identifier,
            );
          } else {
            text = parent.text;
            loading = true;
          }
        } else {
          this.handleInvalidParentModel();
          return;
        }
      }

      currentPath += `/${path}`;

      if (!isSubParent) {
        this.breadcrumbService.add({
          text: text,
          path: currentPath,
          loading: loading,
        });
      }
    });
  }

  isSubParent(parents: any[], path: string) {
    let isSub = false;

    if (this.baseParents.find((bp) => bp == path) != null) return isSub;

    parents.forEach((p) => {
      if (this.baseParents.find((bp) => bp == p.text.toLowerCase()) != null)
        isSub = true;
    });
    return isSub;
  }

  isNotConcreteModel(path: string) : {
    text: string;
    path: string;
    service: AResourceService<any>;
    identifier: string;
  } | null {
    if (this.validParentPaths.indexOf(path) === -1) return null;

    // Mailarchiv: search has no child resources, no service needed
    if (path === 'search') {
      return {
        text: 'SEARCH',
        path: 'search',
        service: null as unknown as AResourceService<any>,
        identifier: 'search',
      };
    }
    if (path === 'admin') {
      return {
        text: 'ADMIN',
        path: 'admin',
        service: null as unknown as AResourceService<any>,
        identifier: 'admin',
      };
    }
    if (path === 'users') {
      return {
        text: 'USERS',
        path: 'users',
        service: null as unknown as AResourceService<any>,
        identifier: 'users',
      };
    }
    if (path === 'mail-accounts') {
      return {
        text: 'MAIL_ACCOUNTS',
        path: 'mail-accounts',
        service: null as unknown as AResourceService<any>,
        identifier: 'mail-accounts',
      };
    }
    if (path === 'settings') {
      return {
        text: 'SETTINGS',
        path: 'settings',
        service: null as unknown as AResourceService<any>,
        identifier: 'settings',
      };
    }

    // TODO: Adjust to be usable in mail archive

    // let service;
    // let identifier = '';
    // switch (path) {
    //   case 'objects':
    //     service = this.injector.get(ObjectService);
    //     identifier = 'objects';
    //     break;
    //   case 'employees':
    //     service = this.injector.get(EmployeeService);
    //     identifier = 'employees';
    //     break;
    //   case 'assignments':
    //     service = this.injector.get(AssignmentService);
    //     identifier = 'assignments';
    //     break;
    //   case 'executions':
    //     service = this.injector.get(ExecutionService);
    //     identifier = 'executions';
    //     break;
    //   case 'teams':
    //     service = this.injector.get(TeamService);
    //     identifier = 'teams';
    //     break;
    //   case 'deficiencies':
    //     service = this.injector.get(DeficiencyService);
    //     identifier = 'deficiencies';
    //     break;
    //   case 'customers':
    //     service = this.injector.get(CustomerService);
    //     identifier = 'customers';
    //     break;
    //   case 'required-employees':
    //     service = this.injector.get(RequiredEmployeeService);
    //     identifier = 'required-employees';
    //     break;
    //   case 'assignment-extensions':
    //     service = this.injector.get(AssignmentExtensionService);
    //     identifier = 'assignment-extensions';
    //     break;
    //   case 'offers':
    //     service = this.injector.get(OfferService);
    //     identifier = 'offers';
    //     break;
    //   case 'invoices':
    //     service = this.injector.get(InvoiceService);
    //     identifier = 'invoices';
    //     break;
    //   case 'materials':
    //     service = this.injector.get(MaterialService);
    //     identifier = 'materials';
    //     break;
    //   case 'used-materials':
    //     service = this.injector.get(UsedMaterialService);
    //     identifier = 'used-materials';
    //     break;
    //   case 'stock-bookings':
    //     service = this.injector.get(StockBookingService);
    //     identifier = 'stock-bookings';
    //     break;
    //   case 'suppliers':
    //     service = this.injector.get(SupplierService);
    //     identifier = 'suppliers';
    //     break;
    //   case 'sick-leaves':
    //     service = this.injector.get(SickLeaveService);
    //     identifier = 'sick-leaves';
    //     break;
    //   case 'device-types':
    //     service = this.injector.get(DeviceTypeService);
    //     identifier = 'device-types';
    //     break;
    //   case 'equipment':
    //     service = this.injector.get(EquipmentService);
    //     identifier = 'equipment';
    //     break;
    //   case 'holidays':
    //     service = this.injector.get(HolidayService);
    //     identifier = 'holidays';
    //     break;
    //   case 'qualifications':
    //     service = this.injector.get(QualificationService);
    //     identifier = 'qualifications';
    //     break;
    //   case 'assignment-types':
    //     service = this.injector.get(AssignmentTypeService);
    //     identifier = 'assignment-types';
    //     break;
    //   case 'execution-teams':
    //     service = this.injector.get(ExecutionTeamService);
    //     identifier = 'execution-teams';
    //     break;
    //   case 'devices':
    //     service = this.injector.get(DeviceService);
    //     identifier = 'devices';
    //     break;
    //   case 'reviews':
    //     service = this.injector.get(ReviewService);
    //     identifier = 'reviews';
    //     break;
    //   case 'object-reviews':
    //     service = this.injector.get(ObjectReviewService);
    //     identifier = 'object-reviews';
    //     break;
    //   case 'assignment-review-ratings':
    //     service = this.injector.get(AssignmentReviewRatingService);
    //     identifier = 'assignment-review-ratings';
    //     break;
    //   case 'maintenance':
    //   case 'maintenances':
    //     service = this.injector.get(MaintenanceService);
    //     identifier = 'maintenances';
    //     break;
    //   case 'device-allocation':
    //   case 'device-allocations':
    //     service = this.injector.get(DeviceAllocationService);
    //     identifier = 'device-allocations';
    //     break;
    //   case 'trainings':
    //     service = this.injector.get(TrainingService);
    //     identifier = 'trainings';
    //     break;
    //   case 'maintenance-types':
    //     service = this.injector.get(MaintenanceTypeService);
    //     identifier = 'maintenance-types';
    //     break;
    //   case 'user-trainings':
    //     service = this.injector.get(UserTrainingService);
    //     identifier = 'user-trainings';
    //     break;
    //   case 'storages':
    //     service = this.injector.get(StorageService);
    //     identifier = 'storages';
    //     break;
    //   case 'additional-options':
    //     service = this.injector.get(AdditionalOptionService);
    //     identifier = 'additional-options';
    //     break;
    //   case 'execution-additional-option-values':
    //     service = this.injector.get(ExecutionAdditionalOptionValueService);
    //     identifier = 'execution-additional-option-values';
    //     break;
    // }
    // // if(path != "dashboard")
    // //   service!.getAll()
    // return {
    //   text: path.toUpperCase(),
    //   identifier: identifier,
    //   path: path,
    //   service: service as AResourceService<any>,
    // };
    return null;
  }

  getConcreteModel(
    id: number,
    parent: {
      text: string;
      path: string;
      service: AResourceService<any>;
      identifier: string;
    },
  ) {
    let model = null;
    model =
      parent.service.models().find((m) => m.id == id) ??
      parent.service.usedConcreteModels().find((m) => m.id == id);

    if (!model) {
      if (parent.service.loading().loading) {
        runInInjectionContext(this.environmentInjector, () => {
          toObservable(parent.service.loading)
            .pipe(
              filter((v) => !v.loading),
              take(1),
              takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
              next: () => {
                model =
                  parent.service.models().find((m) => m.id == id) ??
                  parent.service.usedConcreteModels().find((m) => m.id == id);
                if (model)
                  this.breadcrumbService.setLoadedBreadcrumb(
                    model,
                    this.modelService.getModelLabel(model, parent.identifier),
                    parent.text,
                  );
                else this.fetchConcreteModel(id, parent);
              },
              error: () => {
                this.handleInvalidParentModel();
              },
            });
        });
      } else this.fetchConcreteModel(id, parent);
    }
    return model;
  }

  fetchConcreteModel(
    id: number,
    parent: {
      text: string;
      path: string;
      service: AResourceService<any>;
      identifier: string;
    },
  ) {
    parent.service.getOne(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (model) => {
        this.breadcrumbService.setLoadedBreadcrumb(
          model,
          this.modelService.getModelLabel(model, parent.identifier),
          parent.text,
        );
      },
      error: () => {
        this.handleInvalidConcreteModel(parent);
      },
    });
  }

  handleInvalidParentModel() {
    this.router
      .navigate(['/'])
      .then(() => this.notificationService.error('PAGE_NOT_FOUND'));
  }

  handleInvalidConcreteModel(parent: {
    text: string;
    path: string;
    service: AResourceService<any>;
  }) {
    this.router
      .navigate([parent.path])
      .then(() =>
        this.notificationService.error(
          `NAVIGATION.${parent.text}_RESOURCE_NOT_FOUND`,
        ),
      );
  }
}
